import { defineStore } from 'pinia';
import { ref } from 'vue';
import axios from 'axios';
import moment from 'moment';

const formattedDate = (date: string) => moment(date).format('MM.DD.YYYY');

export const useWordPressStore = defineStore('wordPress', () => {
  const articles = ref(null);
  const media = ref(null);
  const authors = ref(null);
  const wordPressLoading = ref(false);
  const wordPressError = ref(false);

  const wordPressArticles = ref([]);
  const articleMedia = ref([]);
  const articlePage = ref(1);

  const wordPressMedia = ref([]);
  const mediaPage = ref(0);

  const getMediaUrl = (mediaId: number | string) => {
    // If mediaId is already a URL string, return it directly
    if (typeof mediaId === 'string' && mediaId.startsWith('http')) {
      return mediaId;
    }
    const mediaItem = media.value?.find((mi: any) => mi.id === mediaId);
    return mediaItem?.source_url;
  };

  const getAuthorName = (authorId: number) => {
    const author = authors.value?.find((a: any) => a.id === authorId);
    return author?.name;
  };

  const fetchMedia = async () => {
    // Filter to only include integer IDs (not full URLs)
    const mediaIds = articleMedia.value.filter((m: any) => typeof m === 'number');
    const mediaArray = mediaIds.slice(mediaPage.value * 100, mediaPage.value * 100 + 100);

    // Skip API call if no IDs to fetch
    if (mediaArray.length === 0) {
      return [];
    }

    const response = await axios.get(
      `https://www.sportsinfosolutions.com/wp-json/wp/v2/media?per_page=100&include=${mediaArray}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    wordPressMedia.value = [...wordPressMedia.value, ...response.data];

    const allMediaIds = articleMedia.value.filter((m: any) => typeof m === 'number');
    if ((mediaPage.value + 1) * 100 < allMediaIds.length) {
      mediaPage.value += 1;
      await fetchMedia();
    }

    // Add URL-based media items directly
    const urlBasedMedia = articleMedia.value
      .filter((m: any) => typeof m === 'string')
      .map((url: string, index: number) => ({
        id: `url-${index}`,
        source_url: url,
      }));

    const allMedia = [
      ...wordPressMedia.value.map((m: any) => ({
        id: m.id,
        source_url: m.source_url,
      })),
      ...urlBasedMedia,
    ];
    return allMedia;
  };

  const fetchAuthors = async () => {
    const response = await axios.get('https://www.sportsinfosolutions.com/wp-json/wp/v2/users?per_page=100', {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const allAuthors = response.data.map((a: any) => ({
      id: a.id,
      name: a.name,
    }));
    return allAuthors;
  };

  const fetchArticles = async () => {
    const response = await axios.get(
      'https://www.sportsinfosolutions.com/wp-json/wp/v2/posts?per_page=100&after=2024-12-31T13:00:00',
      {
        headers: {
          'Content-Type': 'application/json',
        },
        params: {
          categories: [897],
          page: articlePage.value,
        },
      }
    );

    wordPressArticles.value = [...wordPressArticles.value, ...response.data];
    if (Number(response.headers['x-wp-totalpages']) > articlePage.value) {
      articlePage.value += 1;
      await fetchArticles();
    }

    const articlesFeaturedMedia = wordPressArticles.value.map((a: any) => a.featured_media);
    const articleThumbnails = wordPressArticles.value.map((a: any) =>
      a.acf.post_square_thumbnail ? a.acf.post_square_thumbnail : null
    );
    articleMedia.value = [...articlesFeaturedMedia, ...articleThumbnails];
    articleMedia.value = articleMedia.value.filter((m: any) => m !== null);
  };

  const setArticles = () => {
    const allArticles = wordPressArticles.value.map((a: any) => ({
      id: a.id,
      date: formattedDate(a.date),
      link: a.link,
      title: a.title.rendered,
      excerpt: a.excerpt.rendered,
      author: getAuthorName(a.author),
      featured_media: getMediaUrl(a.featured_media) ?? '/images/SIS_Logo_dark.svg',
      thumbnail: getMediaUrl(a.acf.post_square_thumbnail),
    }));
    return allArticles;
  };

  const fetchArticleById = async (id: number) => {
    try {
      const response = await axios.get(`https://www.sportsinfosolutions.com/wp-json/wp/v2/posts/${id}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (response.status === 200) {
        // Keep pinned article unique if it already exists in paginated results.
        wordPressArticles.value = [
          response.data,
          ...wordPressArticles.value.filter((article: any) => article.id !== response.data.id),
        ];
        articleMedia.value.push(response.data.featured_media);
        articleMedia.value.push(
          response.data.acf.post_square_thumbnail ? response.data.acf.post_square_thumbnail : null
        );
      } else {
        throw new Error(`Error fetching article with id ${id}: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching article by ID:', error);
    }
  };

  const fetchWordPressData = async () => {
    wordPressLoading.value = true;
    try {
      authors.value = await fetchAuthors();
      await fetchArticles();
      await fetchArticleById(1069); // Pin this as second.
      await fetchArticleById(9754); // Pin this as first.
      media.value = await fetchMedia();
      articles.value = setArticles();
    } catch (error) {
      wordPressError.value = true;
    } finally {
      wordPressLoading.value = false;
    }
  };

  return {
    articles,
    media,
    wordPressError,
    wordPressLoading,
    fetchWordPressData,
  };
});
